import { type ReactNode } from 'react'
import { href, Link, Outlet, useLocation } from 'react-router'
import { Icon } from '#app/components/ui/icon.tsx'
import { SyrchaLogo } from '#app/components/ui/syrcha-logo.tsx'
import { UserDropdown } from '#app/components/user-dropdown.tsx'
import { useOptionalUser } from '#app/utils/user.ts'

// Deliberately no Blog link: the header is the buying path, and posts are
// entered from search rather than browsed to from a nav bar. The blog lives in
// the footer's Company column.
const NAV_LINKS = [
	{ to: '/#how', label: 'How it works' },
	{ to: '/#frameworks', label: 'Coverage' },
	{ to: '/#pricing', label: 'Pricing' },
	{ to: '/about', label: 'About' },
]

function Logo() {
	return (
		<Link
			to={href('/')}
			className="group inline-flex items-center gap-2.5 font-[family-name:var(--font-display)]"
		>
			<SyrchaLogo className="h-7 w-7 transition-transform group-hover:rotate-[-3deg]" />
			<span className="text-[15px] font-semibold tracking-tight text-[var(--marketing-black)]">
				Syrcha
			</span>
		</Link>
	)
}

function NavLink({ to, label }: { to: string; label: string }) {
	const { pathname, hash } = useLocation()
	const isHashLink = to.includes('#')
	const active = isHashLink
		? pathname === '/' && hash === to.slice(to.indexOf('#'))
		: pathname === to
	return (
		<Link
			to={to}
			className={`relative inline-flex min-h-6 items-center text-[13px] font-medium tracking-tight transition-colors ${
				active
					? 'text-[var(--marketing-black)]'
					: 'text-[var(--marketing-gray)] hover:text-[var(--marketing-black)]'
			}`}
		>
			{label}
			{active ? (
				<span className="absolute right-0 -bottom-[18px] left-0 h-px bg-[var(--marketing-accent)]" />
			) : null}
		</Link>
	)
}

/**
 * Below `md` the header nav is hidden, which previously left the mobile header
 * with nothing but the logo and "Get started" - no way to reach Pricing or to
 * log in. `<details>` carries the disclosure state, the expanded/collapsed
 * announcement, and keyboard operation natively, so this needs no state, no
 * effect, and no outside-click listener. The panel closes itself on click,
 * which covers the client-side navigations that leave the header mounted.
 */
function MobileNav({ signedIn }: { signedIn: boolean }) {
	return (
		<details className="group relative md:hidden">
			<summary
				className="flex h-11 w-11 cursor-pointer list-none items-center justify-center rounded-md border border-[var(--marketing-border-strong)] text-[var(--marketing-black)] transition-colors hover:border-[var(--marketing-accent)] [&::-webkit-details-marker]:hidden"
				aria-label="Menu"
			>
				<Icon
					name="hamburger-menu"
					className="h-4 w-4 group-open:hidden"
					aria-hidden
				/>
				<Icon
					name="cross-1"
					className="hidden h-4 w-4 group-open:block"
					aria-hidden
				/>
			</summary>
			<nav
				aria-label="Site"
				onClick={(event) =>
					event.currentTarget.closest('details')?.removeAttribute('open')
				}
				className="absolute top-[calc(100%+0.75rem)] right-0 w-60 rounded-lg border border-[var(--marketing-border)] bg-[var(--marketing-elevated)] p-2 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.8)]"
			>
				{NAV_LINKS.map((link) => (
					<Link
						key={link.to}
						to={link.to}
						className="flex min-h-11 items-center rounded-md px-3 text-[14px] font-medium text-[var(--marketing-black)] transition-colors hover:bg-[var(--marketing-accent-tint)]"
					>
						{link.label}
					</Link>
				))}
				{signedIn ? null : (
					<Link
						to={href('/login')}
						className="mt-1 flex min-h-11 items-center rounded-md border-t border-[var(--marketing-border)] px-3 pt-3 font-[family-name:var(--font-mono)] text-[12px] tracking-[0.08em] text-[var(--marketing-gray)] transition-colors hover:text-[var(--marketing-black)]"
					>
						Log in
					</Link>
				)}
			</nav>
		</details>
	)
}

/**
 * `children` exists for the marketing error boundary: React Router renders a
 * layout route's error element *instead of* its component, so the boundary has
 * to mount the chrome itself. Everything else leaves it undefined and gets the
 * `<Outlet />`.
 */
export function MainLayout({ children }: { children?: ReactNode }) {
	const user = useOptionalUser()
	const year = new Date().getFullYear()

	return (
		<div className="marketing-surface dark flex min-h-[100dvh] flex-col bg-[var(--marketing-base)] text-[var(--marketing-black)] antialiased selection:bg-[var(--marketing-accent)] selection:text-[#0b0e13]">
			<a
				href="#main-content"
				className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:rounded focus:bg-[var(--marketing-accent)] focus:px-3 focus:py-1.5 focus:text-[#0b0e13] focus:shadow"
			>
				Skip to main content
			</a>

			<header className="sticky top-0 z-50 border-b border-[var(--marketing-border)]/70 bg-[var(--marketing-base)]/85 backdrop-blur-md">
				<div className="mx-auto flex h-14 max-w-6xl items-center justify-between gap-6 px-6 lg:px-8">
					<Logo />
					<nav className="hidden items-center gap-7 md:flex">
						{NAV_LINKS.map((link) => (
							<NavLink key={link.to} to={link.to} label={link.label} />
						))}
					</nav>
					<div className="flex items-center gap-2 sm:gap-3">
						{user ? (
							<UserDropdown />
						) : (
							<>
								<Link
									to={href('/login')}
									className="hidden min-h-6 items-center text-[13px] font-medium text-[var(--marketing-gray)] transition-colors hover:text-[var(--marketing-black)] md:inline-flex"
								>
									Log in
								</Link>
								<Link
									to={href('/signup')}
									className="inline-flex min-h-11 items-center gap-1.5 rounded-md border border-[var(--marketing-border-strong)] bg-[var(--marketing-elevated)] px-3 font-[family-name:var(--font-mono)] text-[12px] font-medium text-[var(--marketing-black)] transition-colors hover:border-[var(--marketing-accent)] hover:text-[var(--marketing-accent)] md:min-h-0 md:py-1.5"
								>
									Get started
									<span aria-hidden className="text-[var(--marketing-accent)]">
										→
									</span>
								</Link>
							</>
						)}
						<MobileNav signedIn={Boolean(user)} />
					</div>
				</div>
			</header>

			<main
				id="main-content"
				tabIndex={-1}
				className="flex flex-1 flex-col focus:outline-none"
			>
				{children ?? <Outlet />}
			</main>

			<footer className="border-t border-[var(--marketing-border)] bg-[var(--marketing-base)]">
				<div className="mx-auto grid max-w-6xl gap-10 px-6 py-14 md:grid-cols-[1.5fr_1fr_1fr_1fr] lg:px-8">
					<div>
						<Logo />
						<p className="mt-4 max-w-xs text-[13px] leading-relaxed text-[var(--marketing-gray)]">
							Continuous privacy compliance scanning for GDPR, CCPA and the EU
							AI Act. Audit-ready reports without the audit.
						</p>
						{/* "SOC 2 in progress" named no auditor, no scope and no date, so
						    it was a trust badge doing the work this product says it does
						    with mechanism. Both facts here are checkable. */}
						<p className="mt-6 font-[family-name:var(--font-mono)] text-[11px] tracking-[0.18em] text-[var(--marketing-faint)] uppercase">
							Hosted in EU · Public beta
						</p>
					</div>

					<FooterColumn
						title="Product"
						links={[
							{ to: '/', label: 'Overview' },
							{ to: '/#how', label: 'How it works' },
							{ to: '/#frameworks', label: 'Coverage' },
							{ to: '/#pricing', label: 'Pricing' },
							{ to: '/mcp-server', label: 'MCP server' },
						]}
					/>

					<FooterColumn
						title="Company"
						links={[
							{ to: '/about', label: 'About' },
							{ to: '/blog', label: 'Blog' },
							{ to: '/support', label: 'Support' },
							{
								to: user ? '/dashboard' : '/login',
								label: user ? 'Dashboard' : 'Log in',
							},
						]}
					/>

					<FooterColumn
						title="Legal"
						links={[
							{ to: '/privacy', label: 'Privacy' },
							{ to: '/tos', label: 'Terms' },
							{ to: '/refund-policy', label: 'Refunds' },
							{ to: '/aup', label: 'Acceptable use' },
						]}
					/>
				</div>
				<div className="border-t border-[var(--marketing-border)]/70">
					<div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-3 px-6 py-5 text-[12px] text-[var(--marketing-faint)] sm:flex-row sm:items-center lg:px-8">
						{/* The mono slot here used to render `build · stable · YYYY.MM`
						    from `new Date()` - a build identifier that identified no
						    build, on a page whose whole argument is that its numbers
						    trace to something. Wire a real commit or version in if you
						    want the slot back. */}
						{/* Was "Built in the EU", which the company is not - Syrcha is
					    operated from Australia and hosts in Germany. Hosting is a
					    fact about infrastructure; it is not a fact about where the
					    company sits, and conflating them is the exact move the ToS
					    and privacy policy were just corrected for. */}
						<p>© {year} Syrcha. Operated from Australia, hosted in the EU.</p>
					</div>
				</div>
			</footer>
		</div>
	)
}

function FooterColumn({
	title,
	links,
}: {
	title: string
	links: Array<{ to: string; label: string }>
}) {
	return (
		<nav aria-label={title}>
			<h3 className="font-[family-name:var(--font-mono)] text-[11px] font-medium tracking-[0.2em] text-[var(--marketing-faint)] uppercase">
				{title}
			</h3>
			{/* Rows are 44px on touch widths - the 13px links measured 15px tall,
			    under even the 24px AA minimum - and tighten back up at `md`. */}
			<ul className="mt-2 text-[13px] md:mt-4 md:space-y-2.5">
				{links.map((link) => (
					<li key={`${title}-${link.to}-${link.label}`}>
						<Link
							to={link.to}
							className="flex min-h-11 items-center text-[var(--marketing-gray)] transition-colors hover:text-[var(--marketing-black)] md:min-h-6"
						>
							{link.label}
						</Link>
					</li>
				))}
			</ul>
		</nav>
	)
}
